import React, { FC, useCallback } from 'react';

import { cloneDeep } from 'lodash-es';

import { IItemOptionModifier } from '@rbi-ctg/menu';
import { ListCardGroup } from 'components/list-card-group';
import { DEFAULT_SLOT_KEY } from 'components/product-detail-v2/product-detail-flow/product-item-flow/constants';
import { t } from 'state/intl/translations';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { isCombo } from 'utils/menu';

import { ModifierListItem } from './modifier-list-item';
import { IModifierTypeUIProps, IRadioCheckListOption } from './types';
import { generateSelections, logProductModifierCustomization } from './utils';

const setQuantitiesToZero = (data: Record<string, any>) => {
  return Object.keys(data).reduce<Record<string, any>>((acc, key) => {
    acc[key] = { ...data[key], quantity: 0 };
    return acc;
  }, {});
};

export const ModifierMultipleChoices: FC<IModifierTypeUIProps> = ({
  modifier,
  selections,
  onSelectionsChange,
  selectedItem,
}) => {
  const { isEdit, slotKeyToCustomize } = useProductWizardFlow();
  const {
    updateSingleOrMultipleModifierSelection,
    menuObject,
    singleOrMultipleModifierSelection,
  } = useProductWizardContext();

  const slot = slotKeyToCustomize ? slotKeyToCustomize : DEFAULT_SLOT_KEY;
  const modifierSelection = singleOrMultipleModifierSelection?.[slot];

  const isComboMenuObject = isCombo(menuObject);

  const [selectionsList, setSelectionsList] = React.useState(() => {
    if (
      isEdit ||
      !!isComboMenuObject ||
      !modifierSelection?.isPristine ||
      !!modifierSelection.isSaved
    ) {
      return cloneDeep(selections);
    }
    return setQuantitiesToZero(cloneDeep(selections));
  });

  const handleOptionQuantityChange = useCallback(
    (option: IItemOptionModifier, newQuantity: number) => {
      const getSelections = (): Record<string, any> => {
        if (
          isEdit ||
          !!isComboMenuObject ||
          !modifierSelection?.isPristine ||
          !!modifierSelection.isSaved
        ) {
          return cloneDeep(selections);
        }
        return {};
      };

      const selectionsToMergeWith = getSelections();

      const updatedSelections = generateSelections(
        cloneDeep(selectionsToMergeWith),
        option,
        newQuantity,
        modifier.maxAmount
      );

      onSelectionsChange({
        ...updatedSelections,
      });

      updateSingleOrMultipleModifierSelection(slot, { isPristine: false });

      setSelectionsList(updatedSelections);

      logProductModifierCustomization(
        option.name?.locale ?? '',
        modifier.name.locale,
        option._key,
        newQuantity
      );
    },
    [
      modifier.maxAmount,
      modifier.name.locale,
      onSelectionsChange,
      updateSingleOrMultipleModifierSelection,
      slot,
      isEdit,
      isComboMenuObject,
      modifierSelection?.isPristine,
      modifierSelection?.isSaved,
      selections,
    ]
  );

  const radioListOptions: IRadioCheckListOption[] = Array.from({
    length: modifier.maxAmount,
  }).map((_, index) => {
    const i = index + 1;
    return {
      label: i.toString(),
      value: i.toString(),
    };
  });

  return (
    <ListCardGroup
      isFullContainer
      header={t('chooseItemAmount', { amount: modifier.maxAmount, itemName: modifier.name.locale })}
      items={modifier.options}
      renderItem={({ item: option }: { item: IItemOptionModifier }) => {
        const selectedValue = selectionsList[option._key]?.quantity || 0;

        return (
          <ModifierListItem
            key={`${option._key}-${selectedValue}`}
            isSelected={selectedValue !== 0}
            selectedOption={selectedValue.toString()}
            badgeContent={selectedValue !== 0 ? selectedValue.toString() : ''}
            content={option}
            itemOption={modifier}
            selectedItem={selectedItem}
            options={radioListOptions}
            onOptionPress={(newQuantity: number) =>
              handleOptionQuantityChange(option, Number(newQuantity))
            }
            onCardPress={() => {
              const isSelected = selectedValue !== 0;
              const defaultSelectionQuantity = isSelected ? 0 : 1;
              handleOptionQuantityChange(option, defaultSelectionQuantity);
            }}
            multipleChoiceQuantity
          />
        );
      }}
    />
  );
};
